/**
 * Formats backend datetimes for UI (Colombia / America/Bogota).
 *
 * Supports:
 * - ISO with offset: 2026-08-11T08:04:37.000-05:00
 * - ISO with Z: 2026-08-11T08:04:37.000Z
 * - ISO without zone: 2026-08-05T18:12:01 → treated as Colombia time (-05:00)
 * - Fractional seconds longer than ms: ...51.058554
 */
export const TimeFormatter = (fechaBackend: string): string => {
  if (!fechaBackend?.trim()) {
    return "Invalid date";
  }

  let normalized = fechaBackend.trim();

  // JS Date reliably parses up to 3 fractional digits
  normalized = normalized.replace(
    /(\.\d{3})\d+(?=(?:[zZ]|[+-]\d{2}:?\d{2})?$)/,
    "$1"
  );

  const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(normalized);
  // Values without zone are Colombia wall-clock from backend (verified with
  // catalog sync run at 12:04 returning "…T12:04:59"). Bogota has no DST.
  const date = new Date(hasTimezone ? normalized : `${normalized}-05:00`);

  if (Number.isNaN(date.getTime())) {
    return "Invalid date";
  }

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Bogota",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "";

  return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get("minute")}`;
};
